import { queryClient } from "@/ReactQuery/reactQueryClient";
import { INTERACTION_QUERY_KEYS } from "../../Data/interactions";
import {
  beginViewerNavigation,
  isCurrentViewerNavigation,
} from "@/components/FileViewer/services/viewerNavigation";
import { fetchInteraction } from "../api";
import openFile from "../../../../FpAppV4/services/openFile";
import { getViewerMode } from "../../../viewerMode";
import store from "@/redux/store";
import {
  setFileViewerData,
  setFileViewerDataOnlyNew,
} from "@/redux/fileViewerSlice";

/** How the host page wants the open handled. `inlineViewerOnly`: the page
 *  mounts its own `<FileViewer />` (the named agents' split pane), so the
 *  open must fill the shared viewer state and nothing else — never the
 *  global drawer, whatever the surface's mode says. Without it a citation
 *  from that page opened the document twice, side by side. */
export interface OpenInViewerOptions {
  inlineViewerOnly?: boolean;
}

/**
 * The evidence anchor a citation click carries (#11496): what the note viewer
 * highlights and scrolls to. Same fields the document open path already
 * forwards into the viewer slice.
 */
interface CitationHighlight {
  highlightContext?: string;
  originalNumValue?: string;
  parentElementText?: string;
  // The value where the note prints it, or nothing (#12090).
  exactOnly?: boolean;
}

/**
 * Open a file in whichever presentation the current surface is set to (#9746).
 *
 * This is the single entry point for opening a document. Presentation used to
 * be picked by which function a call site reached for — `openFile` landed in
 * the overlay drawer, this module's event landed in the split pane — which is
 * why the same document opened differently depending on where it was clicked.
 * The mode is now read here instead, so every caller stays presentation-blind.
 */
function openInViewer(
  fileId: string,
  fileName: string,
  fileType: string,
  highlight?: CitationHighlight,
  options?: OpenInViewerOptions,
  interactionId?: string
) {
  const inlineViewerOnly = options?.inlineViewerOnly ?? false;
  if (
    !inlineViewerOnly &&
    getViewerMode(window.location.pathname) === "overlay"
  ) {
    // Redux without `doNotOpenDrawer`; the globally mounted DocumentViewDrawer
    // takes it from there.
    openFile({ fileId, fileName, fileType, interactionId });
    // Merged after the open so a citation into an already-open note still
    // refreshes the highlight (openFile early-returns in that case).
    if (highlight) {
      store.dispatch(setFileViewerData({ ...highlight }));
    }
    return;
  }
  window.dispatchEvent(
    new CustomEvent("openDocumentPanel", {
      detail: {
        fileId,
        interactionId,
        fileName,
        fileType,
        isDirectFileOpen: true,
        inlineViewerOnly,
        ...highlight,
      },
    })
  );
}

/**
 * Open a document (blob) in the file viewer.
 */
export function openDocumentInViewer(
  fileId: string,
  fileName: string,
  blobKind?: string
) {
  beginViewerNavigation();
  const fileType = blobKind || fileName.split(".").pop() || "";
  openInViewer(fileId, fileName, fileType);
}

/**
 * Open a note-only interaction (no backing blob) in the note viewer, keyed by
 * the interaction's public id (#11496). Same presentation split as
 * `openInViewer`, but the viewer state carries `interactionId` instead of a
 * blob id — the note content is fetched live from the interaction API.
 */
function openInteractionNoteInViewer(
  interactionId: string,
  interactionTitle: string,
  highlight?: CitationHighlight,
  options?: OpenInViewerOptions
) {
  const inlineViewerOnly = options?.inlineViewerOnly ?? false;
  if (
    !inlineViewerOnly &&
    getViewerMode(window.location.pathname) === "overlay"
  ) {
    store.dispatch(
      setFileViewerDataOnlyNew({
        isOpen: true,
        fileType: "finpilot-interaction",
        fileName: interactionTitle,
        interactionId,
        ...highlight,
      })
    );
    return;
  }
  window.dispatchEvent(
    new CustomEvent("openDocumentPanel", {
      detail: {
        interactionId,
        fileName: interactionTitle,
        fileType: "finpilot-interaction",
        inlineViewerOnly,
        ...highlight,
      },
    })
  );
}

/**
 * Open an interaction in the file viewer.
 * Fetches the interaction; opens its backing document when it has one, or the
 * live note view keyed by interaction id when it doesn't (#11496). Either way
 * the reader stays on the page they clicked from — this never navigates to
 * the editable /rms/interaction record page.
 *
 * `highlight` carries a citation's evidence anchor so the note viewer lands
 * highlighted on the cited passage; RMS card call sites omit it.
 */
export async function openInteractionInViewer(
  interactionId: string,
  interactionTitle: string,
  highlight?: CitationHighlight,
  options?: OpenInViewerOptions
) {
  const intent = beginViewerNavigation();
  let interaction;
  try {
    interaction = await fetchInteraction(interactionId);
  } catch {
    if (isCurrentViewerNavigation(intent)) {
      openInteractionNoteInViewer(
        interactionId,
        interactionTitle,
        highlight,
        options
      );
      store.dispatch(
        setFileViewerData({
          interactionError: "Unable to load this interaction",
        })
      );
    }
    return;
  }
  if (!isCurrentViewerNavigation(intent)) return;
  const queryKey = INTERACTION_QUERY_KEYS.detail(interactionId);
  // The opener has just authorized and resolved this exact row. Reuse that
  // response so a recently cached sibling selection cannot restore old content
  // or an obsolete backing document, and cancel older in-flight reads first.
  await queryClient.cancelQueries({ queryKey, exact: true });
  if (!isCurrentViewerNavigation(intent)) return;
  queryClient.setQueryData(queryKey, interaction);
  if (interaction.document_id) {
    openInViewer(
      interaction.document_id,
      interactionTitle,
      interaction.document_blob_kind || "finpilot-interaction",
      highlight,
      options,
      interactionId
    );
  } else {
    openInteractionNoteInViewer(
      interactionId,
      interactionTitle,
      highlight,
      options
    );
  }
}
